<template>
	<div id="clientUsers" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Users">
			<button v-if="authUser.permissions.includes('USER_UPDATE')" @click="logoutUsers()" class="hu-pill" title="Logout all users">
				<font-awesome-icon :icon="['fas', 'door-open']" />
				<span>Logout All</span>
			</button>
			<button
				v-if="authUser.permissions.includes('USER_CREATE')"
				@click="openCreateOrUpdateUserModal()"
				class="hu-pill"
				title="Create user"
			>
				<font-awesome-icon :icon="['fas', 'user-plus']" />
				<span>Create User</span>
			</button>
			<button
				v-if="authUser.permissions.includes('USER_ACCESS') && authUser.permissions.includes('USER_CREATE')"
				@click="openAddExistingUserModal()"
				class="hu-pill"
				title="Add existing user"
			>
				<font-awesome-icon :icon="['fas', 'address-card']" />
				<span>Add Existing</span>
			</button>
		</JuniperPageHeader>
		<div class="ju-users-wrap">
			<div class="ju-users-intro mt-3">
				Manage client access, account status, and quick security actions for all assigned users.
			</div>

			<div class="ada-card mt-3">
				<div v-if="loading" class="act-empty">
					<span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading…
				</div>
				<div v-else-if="!users.length" class="act-empty">Client does not have any users yet.</div>
				<div v-else class="act-table-wrap">
					<table class="act-table">
						<thead>
							<tr>
								<th class="act-col-id">#</th>
								<th>Name</th>
								<th>Email</th>
								<th>Roles</th>
								<th class="text-nowrap">Active Token</th>
								<th class="text-nowrap">Last Activity</th>
								<th>Status</th>
								<th class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="user in users" :key="user.id + '-key'" class="act-row">
								<td class="act-col-id act-muted">{{ user.id }}</td>
								<td>
									<a class="ju-user-link" role="button" @click="updateUserAction = true, openCreateOrUpdateUserModal(user)">{{ user.name }}</a>
								</td>
								<td class="act-muted">{{ user.email }}</td>
								<td>{{ user.roles.length ? user.roles.map(role => role.name).join(', ') : '—' }}</td>
								<td>
									<font-awesome-icon v-if="user.hasActiveToken" class="text-success" :icon="['fas', 'check-circle']" />
									<font-awesome-icon v-else class="text-danger" :icon="['fas', 'times-circle']" />
								</td>
								<td class="text-nowrap act-muted">
									{{ user.lastLogin || 'more then 8 hours' }}
								</td>
								<td>
									<span v-if="user.active" class="act-badge act-badge--active">Active</span>
									<span v-else class="act-badge act-badge--inactive">Inactive</span>
								</td>
								<td class="text-nowrap text-end act-actions">
									<button @click="sendPasswordResetLink(user)" class="act-row-btn" data-bs-toggle="tooltip" data-bs-placement="top" title="Send password reset link">
										<font-awesome-icon :icon="['far', 'paper-plane']" />
									</button>
									<button
										class="act-row-btn"
										data-bs-toggle="tooltip" data-bs-placement="top"
										v-if="authUser.permissions.includes('USER_UPDATE')"
										@click="updateUserAction = true, openCreateOrUpdateUserModal(user)"
										role="button"
										title="Edit"
									>
										<font-awesome-icon :icon="['fas', 'edit']" />
									</button>
									<button @click="logoutUsers(user.id)" class="act-row-btn" data-bs-toggle="tooltip" data-bs-placement="top" title="Logout user">
										<font-awesome-icon :icon="['fas', 'door-open']" />
									</button>
									<button @click="disconnectUser(user.id)" class="act-row-btn act-row-btn--warning" data-bs-toggle="tooltip" data-bs-placement="top" title="Disconnect user">
										<font-awesome-icon :icon="['fas', 'user-minus']" />
									</button>
									<button @click="toggleUserActiveStatus(user)" class="act-row-btn" data-bs-toggle="tooltip" data-bs-placement="top" :class="user.active ? 'act-row-btn--danger' : 'act-row-btn--success'" :title="user.active ? 'Deactivate user account' : 'Activate user account'">
										<font-awesome-icon v-if="user.active" :icon="['fas', 'user-times']" />
										<font-awesome-icon v-else :icon="['fas', 'user-check']" />
									</button>
									<button @click="deleteUser(user)" class="act-row-btn act-row-btn--danger" data-bs-toggle="tooltip" data-bs-placement="top" title="Delete user">
										<font-awesome-icon :icon="['fas', 'trash']" />
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Modal for adding Contact -->
		
		<CreateOrUpdateUserModal ref="CreateOrUpdateUserModal" :update="updateUserAction" :userForChildComponent="user" @userSaved="handleUserSaved" @createOrUpdateUserModalClosed="handleCreateOrUpdateUserModalClosed" />
		<AddExistingUserModal ref="AddExistingUserModal" @usersAdded="handleUsersAdded" />
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import AddExistingUserModal from '../../../components/juniper/modals/AddExistingUserModal.vue'
import CreateOrUpdateUserModal from '../../../components/juniper/modals/CreateOrUpdateUserModal.vue'
import { Tooltip } from "bootstrap";
//import restApi from '../../../api/index'

export default {
	name: 'ClientUsers',
	data() {
		return {
			loading: false,
			client: {},
			users: [],
			user: {
				email: '',
				username: '',
				password: '',
				password_confirmation: '' },
			updateUserAction: false,
			roles: [
				{
					id: 1,
					name: 'Editor' },
			] }
	},
	components: { AddExistingUserModal, CreateOrUpdateUserModal },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']) },

	mounted() {
		
	},

	updated() {
		let tooltopElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');

		tooltopElements.forEach(element => {

			let tooltip = Tooltip.getInstance(element);

			if (tooltip) {
				tooltip.dispose();
			}

			if (!Tooltip.getInstance(element)) {
				new Tooltip(element)
			}
		});
	},

	created() {
		this.getClient()
	},
	methods: {
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.getUsers()
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		// Get users
		getUsers() {
			juniperApi
				.get(`/users?client_id=${this.$route.params.id}&clients=${this.$route.params.id}`)
				.then(({ data }) => {
					this.users = data.users
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		async sendPasswordResetLink(user) {

			this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                text: `Are you sure you want to send reset password link to this user?`,
                confirmButtonText: "Yes, send it!",
                showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: "#0FCB4F",
				cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await juniperApi.post('forgot-password', { site_url: this.client.staging ? this.client.staging_url : this.client.url, email: user.email });

                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading() }).then(async (result) => {
                if (result.isConfirmed) {
					console.log(result)
					if (result.value && result.value.data && result.value.data.status && result.value.data.status == 'We have emailed your password reset link!') {
						
						this.$swal("Sent!", `Email with password reset link sent to user!`, "success").then(() => {
	
						});
						this.loading = true;
						this.getUsers();
					} else if(result.value && result.value.data && result.value.data.type && result.value.data.type == 'error') {
						console.log(result.value.data.errors)
						this.$swal("Request failed!", result.value.data.errors && result.value.data.errors.status ? result.value.data.errors.status : "There was a problem, password reset link wasn't send.", "error");
					} else {
						this.$swal("Request failed!", "There was a problem, password reset link wasn't send.", "error");
					}
                }
            });
		},		

		openCreateOrUpdateUserModal(user = this.user) {
			this.user = user;
            this.$refs.CreateOrUpdateUserModal.showModal(true);
        },

		openAddExistingUserModal() {
            this.$refs.AddExistingUserModal.showModal(true);
        },

		handleUsersAdded() {
			this.loading = true;
			this.getUsers();
		},

		handleUserSaved() {
			this.loading = true;
			this.getUsers();
		},

		disconnectUser(id) {
			this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                text: `Are you sure you want to remove user from this client's user list?`,
                confirmButtonText: "Yes, remove it!",
                showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: "#0FCB4F",
				cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await juniperApi.post(`/${this.$route.params.id}/users/${id}/remove-from-client`, {});

                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading() }).then(async (result) => {
                if (result.isConfirmed) {
					if (result.value && result.value.data == 'success') {
						
						this.$swal("Removed!", `User removed from client's user list!`, "success").then(() => {
	
						});
						this.loading = true;
						this.getUsers();
					} else {
						this.$swal("Request failed!", "There was a problem, user wasn't removed.", "error");
					}
                }
            });
		},
		toggleUserActiveStatus(user) {
			this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                text: `Are you sure you want to make this user's account ${user.active ? 'inactive' : 'active'}?`,
                confirmButtonText: `Yes, make it ${user.active ? 'inactive' : 'active'}!`,
                showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: "#0FCB4F",
				cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await juniperApi.post(`/users/${user.id}/${user.active ? 'deactivate' : 'activate'}`, {});

                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading() }).then(async (result) => {
                if (result.isConfirmed) {
					if (result.value && result.value.data == 'success') {
						
						this.$swal(`${user.active ? 'Deactivated' : 'Activated'}!`, `User ${user.active ? 'deactivated' : 'activated'} successfully!`, "success").then(() => {
	
						});
						this.loading = true;
						this.getUsers();
					} else {
						this.$swal("Request failed!", `There was a problem, user's account wasn't ${user.active ? 'deactivated' : 'activated'}.`, "error");
					}
                }
            });
		},
		deleteUser(user) {
			this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                html: `Are you sure you want to delete user <b>${user.name}</b>?`,
                confirmButtonText: "Yes, delete the user!",
                showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: "#0FCB4F",
				cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await juniperApi.delete(`/users/${user.id}`, {});

                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading() }).then(async (result) => {
                if (result.isConfirmed) {
					if (result.value && result.value.data == 'success') {
						
						this.$swal("Deleted!", `User deleted successfully!`, "success");
						this.loading = true;
						this.getUsers();
					} else {
						this.$swal("Request failed!", "There was a problem, user wasn't deleted.", "error");
					}
                }
            });
		},

		logoutUsers(id = null) {
			let ids = []; 
			if (id) {
				ids.push(id);
			} else {
				ids = this.users.map(user => user.id);
			}

			this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                text: `Are you sure you want to logout user/s?`,
                confirmButtonText: "Yes, I am!",
                showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: "#0FCB4F",
				cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await juniperApi.post(`users/sanctum/revoke-token`, {users: ids});

                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading() }).then(async (result) => {
                if (result.isConfirmed) {
					if (result.value && result.value.data == 'success') {
						
						this.$swal("Logged out!", `User/s loggout successfully!`, "success");
						this.loading = true;
						this.getUsers();
					} else {
						this.$swal("Request failed!", "There was a problem, user/s wasn't logged out.", "error");
					}
                }
            });
		},
		
		handleCreateOrUpdateUserModalClosed() {
			this.user = {
                email: '',
				username: '',
				password: '',
				password_confirmation: ''
			};
			this.updateUserAction = false;
		} } }
</script>
<style scoped>
	#clientUsers {
		padding: 0 0 60px;
		margin-top: 0 !important;
	}

	.jc-view {
		width: 100%;
	}

	/* Header action buttons — match the Dashboard/Website quick-action pills */
	.hu-pill {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 7px 12px;
		border: 1px solid #e0e1e1;
		border-radius: 999px;
		background: #ffffff;
		color: #64748b;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
		cursor: pointer;
		transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	}

	.hu-pill:hover {
		background: #f8f9fa;
		border-color: #d1d5db;
		color: #475569;
	}

	.hu-pill svg {
		font-size: 12px;
	}

	.ju-users-intro {
		border: 1px solid #e6e8ec;
		border-radius: 12px;
		padding: 12px 14px;
		color: #4b5563;
		font-size: 13px;
		background: transparent;
	}

	/* Card + compact flat table (settings language, mirrors /activities) */
	.ada-card {
		border: 1px solid #e6e8ec;
		border-radius: 12px;
		padding: 1rem 1.1rem;
		background: transparent;
	}

	.act-empty {
		text-align: center;
		padding: 1.5rem 1rem;
		color: #8a909c;
		font-size: 0.9rem;
	}

	.act-table-wrap {
		overflow-x: auto;
	}

	.act-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 13px;
	}

	.act-table thead th {
		text-align: left;
		font-weight: 600;
		color: #6c757d;
		padding: 4px 10px 8px 0;
		border-bottom: 1px solid #dee2e6;
		white-space: nowrap;
	}

	.act-table tbody td {
		padding: 7px 10px 7px 0;
		border-bottom: 1px solid #f1f3f5;
		vertical-align: middle;
		color: #2b303b;
	}

	.act-table tbody tr:last-child td {
		border-bottom: none;
	}

	.act-row:hover td {
		background: #f3f5f9;
	}

	.act-col-id {
		width: 3.5rem;
	}

	.act-muted {
		color: #8a909c;
	}

	.act-badge {
		display: inline-block;
		padding: 1px 9px;
		font-size: 11px;
		font-weight: 700;
		border-radius: 999px;
	}

	.act-badge--active {
		background: #d1e7dd;
		color: #0f5132;
	}

	.act-badge--inactive {
		background: #f8d7da;
		color: #842029;
	}

	/* Row action icons — plain gray, colour on hover (settings language) */
	.act-actions {
		white-space: nowrap;
	}

	.act-row-btn {
		background: none;
		border: none;
		color: #adb5bd;
		padding: 4px 6px;
		border-radius: 6px;
		cursor: pointer;
		font-size: 13px;
		line-height: 1;
		transition: color 0.15s ease, background 0.15s ease;
	}

	.act-row-btn + .act-row-btn {
		margin-left: 2px;
	}

	.act-row-btn:hover {
		color: #2b303b;
		background: #f3f5f9;
	}

	.act-row-btn--danger:hover {
		color: #dc3545;
		background: #fdecee;
	}

	.act-row-btn--success:hover {
		color: #198754;
		background: #e6f4ec;
	}

	.act-row-btn--warning:hover {
		color: #b8860b;
		background: #fdf6e3;
	}

	.ju-user-link {
		color: #1565c0;
		font-weight: 600;
		text-decoration: none;
	}

	.ju-user-link:hover {
		text-decoration: underline;
	}
</style>
